import { FC, isValidElement, useEffect, useMemo, useState } from "react";
import { Box, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Visibility from "@mui/icons-material/Visibility";
import Image from "next/image";

import UIText from "@/components/text/UIText";
import UIButtonTooltip from "@/components/buttons/pdfButons/UIButtonToltip";
import { RowTable } from "@/components/table/UITableProps";
import { isEmpty } from "@/utils/functions";
import colors from "@/utils/colorsV2";

export interface Action {
  label: string | ((row: RowTable) => string);
  default?: boolean;
  onClick: (row: RowTable) => Promise<void>;
  icon?: string | React.ReactNode | ((row: RowTable) => React.ReactNode);
  disabled: (row: RowTable) => boolean;
  section?: string;
  show?: (row: RowTable) => boolean;
}

interface UIMenuActionsProps {
  actions: Action[];
  solicitud: RowTable;
}

interface IconImgProps {
  icon: string;
  disabled: boolean;
}
const IconImg: FC<IconImgProps> = ({ icon, disabled }) => (
  <Image
    src={icon}
    alt="icon"
    width={16}
    height={16}
    color={disabled ? colors.grayLM200 : colors.asertaBlueGray}
  />
);

const renderIcon = (
  icon: string | React.ReactNode | ((row: RowTable) => string | React.ReactNode),
  row: RowTable
): React.ReactNode => {

  // 1. Si es función, ejecutarla
  if (typeof icon === "function") {
    const result = icon(row);
    return renderIcon(result, row); // Recursivo, pero controlado
  }

  // 2. Si es string → es un icono (verificar que no esté vacío)
  if (typeof icon === "string" && icon.trim() !== "") {
    return <IconImg icon={icon} disabled={false} />;
  }

  // 3. Si es un elemento válido de React
  if (isValidElement(icon)) {
    return icon;
  }

  // 4. Para null / undefined / cualquier basura o string vacío
  return <></>;
};

const getLabelText = (label: string | ((row: RowTable) => string), row: RowTable) => {
  if (typeof label === "string") {
    return label;
  }

  return label(row);
}

const UIMenuActions: FC<UIMenuActionsProps> = ({ actions, solicitud }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Derivar valores en vez de tener múltiples estados
  const visibleActions = useMemo<Action[]>(
    () => actions.filter((a) => !a.show || a.show(solicitud)),
    [actions, solicitud]
  );

  const defaultAction = useMemo<Action | null>(
    () => visibleActions.find((a) => a.default) ?? null,
    [visibleActions]
  );

  const actionsList = useMemo<Action[]>(
    () => visibleActions.filter((a) => !a.default),
    [visibleActions]
  );

  const sections = useMemo(() => {
    const grouped = new Map<string, Action[]>();
    visibleActions.forEach((a) => {
      if (a.section) {
        if (!grouped.has(a.section)) grouped.set(a.section, []);
        grouped.get(a.section)?.push(a);
      }
    });
    return grouped;
  }, [visibleActions]);

  const disabled = useMemo(
    () => actionsList.every((a) => a.disabled(solicitud)),
    [actionsList, solicitud]
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const clickHandler = (action: Action) => {
    handleClose();
    action.onClick(solicitud);
  };

  /** Helpers UI **/
  const renderMenuItems = (list: Action[]) =>
    list.map((action, index) => (
      <MenuItem
        key={index}
        onClick={() => clickHandler(action)}
        disabled={action.disabled(solicitud)}
        sx={{ "&.Mui-disabled": { cursor: "not-allowed" } }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {renderIcon(action.icon, solicitud)}
          <UIText
            text={getLabelText(action.label, solicitud)}
            color="blueGray"
            size="text-xs"
            weight="semibold"
          />
        </Box>
      </MenuItem>
    ));

  const renderTooltipButton = (
    action: Action,
    icon?: React.ReactNode,
    tooltip?: string
  ) => (
    <Tooltip
      title={
        <UIText
          text={tooltip ?? getLabelText(action.label, solicitud)}
          color="white"
          weight="semibold"
          size="text-xs"
        />
      }
      placement="top"
      arrow
    >
      <Box component="span"
        sx={{
          cursor: action.disabled(solicitud) ? "not-allowed" : "pointer",
        }}
      >
        <IconButton
          onClick={() => action.onClick(solicitud)}
          disabled={action.disabled(solicitud)}
          sx={{
            "&:hover": {
              backgroundColor: colors.grayLM200,
            },
            "&.Mui-disabled": {
              cursor: "not-allowed",
              opacity: "0.2"
            }
          }}
        >
          {icon ?? renderIcon(action.icon!, solicitud)}
        </IconButton>
      </Box>
    </Tooltip>
  );

  /** Render principal **/

  // Caso especial cuando defaultAction es "link"
  if (defaultAction?.icon === "link") {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          "&:hover": { opacity: 0.8 },
        }}
        onClick={() => defaultAction.onClick(solicitud)}
      >
        <UIText
          text={getLabelText(defaultAction.label, solicitud)}
          weight="semibold"
          size="text-s"
          color="orange"
        />
      </Box>
    );
  }

  // Caso con secciones
  if (actions.some((a) => !isEmpty(a.section))) {
    return (
      <Box sx={{ display: "flex", gap: 2 }}>
        <UIButtonTooltip
          tooltipContent="Ver Acciones"
          onClick={handleClick}
          onlyIcon
          startIcon={<MoreVertIcon />}
        />
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              style: {
                maxHeight: 48 * 10,
                backgroundColor: colors.white,
              },
            },
          }}
        >
          {Array.from(sections.entries()).map(([section, list], idx, arr) => (
            <Box key={section}>
              <Box sx={{ px: 1.5 }}>
                <UIText
                  text={section}
                  color="blueGray"
                  size="text-xs"
                  weight="semibold"
                />
              </Box>
              {renderMenuItems(list)}
              {idx < arr.length - 1 && <Divider />}
            </Box>
          ))}
        </Menu>
      </Box>
    );
  }

  // Caso con defaultAction y lista de acciones
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {defaultAction && renderTooltipButton(defaultAction)}

      {actionsList.length === 1 &&
        renderTooltipButton(actionsList[0], undefined)}

      {actionsList.length > 1 && (
        <>
          <Tooltip
            title={
              <UIText
                text="Ver más acciones"
                color="white"
                weight="semibold"
                size="text-xs"
              />
            }
            placement="top"
            arrow
          >
            <Box component="span">
              <IconButton
                aria-label="more"
                aria-controls={open ? "long-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                disabled={disabled}
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                style: {
                  maxHeight: 48 * 10,
                  backgroundColor: colors.white,
                },
              },
            }}
          >
            {renderMenuItems(actionsList)}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default UIMenuActions;
